import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { Theme } from "../../types";
import { useThemeSettings } from "../../context/ThemeContext";
import styles from "./Nav.module.scss";

const NAV_ITEMS = [
  { path: "/", key: "home", label: "hi" },
  // { path: "/work", key: "work", label: "work" },
  // { path: "/notes", key: "notes", label: "notes" },
  { path: "/now", key: "now", label: "now" },
  { path: "/about", key: "about", label: "about" },
] as const;

interface NavProps {
  theme: Theme;
}

export function Nav({ theme }: NavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { dark, setDark } = useThemeSettings();
  const [open, setOpen] = useState(false);

  const activeKey = location.pathname.startsWith("/work")
    ? "work"
    : location.pathname.startsWith("/notes")
      ? "notes"
      : location.pathname === "/"
        ? "home"
        : location.pathname.slice(1);

  const go = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <header
      className={styles.header}
      style={{
        background: theme.dark
          ? "rgba(58, 47, 39, 0.72)"
          : "rgba(244, 238, 223, 0.72)",
        borderBottomColor: theme.rule,
      }}
    >
      <button
        onClick={() => go("/")}
        className={styles.logo}
        style={{
          background: theme.card,
          border: `1px solid ${theme.rule}`,
          color: theme.fg,
        }}
      >
        <span
          className={styles.logoOrb}
          style={{
            background: `radial-gradient(circle at 30% 30%, ${theme.butter}, ${theme.acc})`,
          }}
        />
        <span>
          miceha
          <span style={{ color: theme.muted, fontWeight: 400 }}>.codes</span>
        </span>
      </button>

      <button
        onClick={() => setOpen((o) => !o)}
        className={styles.burger}
        aria-label="Menu"
        aria-expanded={open}
        style={{
          border: `1px solid ${theme.rule}`,
          background: theme.card,
        }}
      >
        <span className={styles.burgerLines}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={styles.burgerLine}
              style={{
                top: i * 5,
                background: theme.fg,
                transform: open
                  ? i === 0
                    ? "translateY(5px) rotate(45deg)"
                    : i === 2
                      ? "translateY(-5px) rotate(-45deg)"
                      : "scale(0)"
                  : "none",
                opacity: open && i === 1 ? 0 : 1,
              }}
            />
          ))}
        </span>
      </button>

      <nav
        className={styles.nav}
        style={{ background: theme.card, border: `1px solid ${theme.rule}` }}
      >
        {NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            onClick={() => go(item.path)}
            className={styles.navBtn}
            style={{
              color: activeKey === item.key ? "#fff" : theme.muted,
              background: activeKey === item.key ? theme.acc : "transparent",
              fontWeight: activeKey === item.key ? 600 : 400,
            }}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className={styles.right}>
        <div
          className={styles.statusPill}
          style={{
            background: theme.card,
            border: `1px solid ${theme.rule}`,
            color: theme.muted,
          }}
        >
          <span className={styles.statusDot} />
          available · october '26
        </div>
        <button
          className={styles.darkToggle}
          style={{
            background: theme.card,
            border: `1px solid ${theme.rule}`,
            color: theme.muted,
          }}
          onClick={() => setDark(!dark)}
          title={dark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {dark ? "☀" : "◑"}
        </button>
      </div>

      {open && (
        <nav
          className={styles.burgerMenu}
          style={{
            background: theme.dark
              ? "rgba(58, 47, 39, 0.97)"
              : "rgba(244, 238, 223, 0.97)",
            borderBottom: `1px solid ${theme.rule}`,
          }}
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() => go(item.path)}
              className={styles.burgerMenuBtn}
              style={{
                color: activeKey === item.key ? "#fff" : theme.fg,
                background: activeKey === item.key ? theme.acc : theme.card,
                fontWeight: activeKey === item.key ? 600 : 500,
              }}
            >
              {item.label}
            </button>
          ))}
          <div className={styles.burgerStatus} style={{ color: theme.muted }}>
            <span className={styles.burgerDot} />
            available · october '26
          </div>
        </nav>
      )}
    </header>
  );
}
